import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Cloud, Server, AlertTriangle } from "lucide-react";
import { api } from "@/api/client";
import { HtmlArchiveSearchCard } from "@/components/HtmlArchivePreview";

// 2026-10-25 owner：「直接使用 APP04 的模板 >> 來新增一個導覽 關於此 >> 將內容放入」
// v0.2.0 2026-10-26 owner：「NCB 他數據?? 跟你說完成?! 0」「你後台怎樣確認哪一些資料雲端有被備份成功 NCB 有備份?!」 — NCB／Drive per page, live
// Live numbers come from GET /api/data-browser/storage-overview (login required). Disk / Google Drive / NCB
// sizes cannot be measured from inside the app, so they are a dated snapshot below.

interface Overview {
  generated_at: string;
  projects: { project_id: string; name: string }[];
  keyword_groups: { keyword: string; location_code: number; language_code: string; project_id: string | null; analyses: number; last_at: string; serp_urls: number; urls_with_html: number; llm_steps: number }[];
  html_by_project: { project_id: string | null; versions: number; urls: number; stored_bytes: number }[];
  db: { total_bytes: number; categories: { html: number; serp: number; analysis: number; other: number } };
  archive?: Archive | null;
}
// Per-page archive status, written every 10 minutes on VPS#2 from the NCB / Drive ledgers (each page read back + fingerprint-checked).
interface Archive {
  generated_at: string; html_versions_total: number | null; verified: number; failed: number; daily_sync: string;
  row_limit: number; warn_at: number; last_archived_at?: string;
  databases: { instance: string; name: string; bound: boolean; rows: number; level: "ok" | "warn" | "full" }[];
  by_project: { project_id: string | null; verified: number }[];
  drive?: { verified: number; not_verified: number; by_project: { project_id: string | null; verified: number }[]; remote: string; last_uploaded_at?: string };
  sync?: { last_check_at?: string; ncb_due?: number; ncb_waiting_settle?: number; ncb_note?: string };
}

const SNAPSHOT_AT = "2026-10-24";
const STORAGE = [
  { where: "VPS#2 主機", icon: Server, item: "系統碟已用", gb: 120.94, note: "總 154.2 GB，剩 25.4 GB（83%）；10-24 已刪 5 份舊備份" },
  { where: "VPS#2 主機", icon: Server, item: "APP04 本機資料庫備份", gb: 15.15, note: "每日 3 份＋每週 1 份；每天 12:00 再增約 3.8 GB" },
  { where: "VPS#2 主機", icon: Server, item: "APP04 9/23 資料庫傾印", gb: 3.5, note: "回滾用，待確認可否刪除" },
  { where: "Google Drive", icon: Cloud, item: "VPS2-PG-Backup（全部 APP）", gb: 778.94, note: "每日／每週／每月整庫備份，從不刪" },
  { where: "Google Drive", icon: Cloud, item: "VPS2_Backups/pg（全部 APP）", gb: 670.4, note: "與上一份重複" },
];
const PLAN = [
  { step: "B1-b", item: "本機只留 3 份 APP04 每日備份", status: "完成", when: "10-24" },
  { step: "還原演練", item: "Google Drive 備份還原到臨時庫，84/84 表筆數相符", status: "完成", when: "10-24" },
  { step: "H0", item: "全部 21,550 筆 HTML 指紋清單", status: "完成", when: "10-24" },
  { step: "N0", item: "NCB 子帳號建 app04_html_archive_001～005 與資料表 html_snapshots", status: "完成", when: "10-25" },
  { step: "N1–N2", item: "換上新密鑰、試寫 1 筆並讀回比對指紋", status: "完成", when: "10-25" },
  { step: "N3", item: "全部 21,550 頁送 NCB（逐頁讀回比對指紋；本機不刪）", status: "完成", when: "10-26" },
  { step: "S1", item: "每頁抓完 10～20 分鐘內自動送 NCB，再送 Google Drive 逐頁複本（兩邊比對指紋）", status: "啟用中", when: "10-26 起" },
  { step: "N4", item: "本機沒有的 HTML 改去 NCB 取回", status: "待授權", when: "N3 後約半天" },
  { step: "N5", item: ">7 天舊 HTML 轉存 NCB 並刪本機（每網址留最新 1 版）", status: "待授權", when: "N4 後分批數天" },
  { step: "B1-a", item: "每日備份排除 HTML 表、HTML 改週備（全 APP 備份視窗）", status: "另一視窗", when: "—" },
];
const REGION: Record<number, string> = { 2158: "TW", 2840: "US", 2392: "JP", 2410: "KR", 2156: "CN", 2344: "HK", 2702: "SG", 2458: "MY", 2036: "AU", 2826: "GB", 2124: "CA", 2276: "DE", 2250: "FR", 2356: "IN", 2764: "TH", 2704: "VN", 2360: "ID", 2608: "PH" };
const NO_PROJECT = "（無專案）";
const fmt = (n: number) => n.toLocaleString("en-US");
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
const gb = (b: number) => (b / 1e9).toFixed(2);
const mb = (b: number) => (b / 1048576).toLocaleString("en-US", { maximumFractionDigits: 1 });
const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

type ProjRow = { name: string; keywords: number; groups: number; serp: number; last: string | null; over30: number; serpUrls: number; withHtml: number; htmlUrls: number; versions: number; bytes: number };
type SortKey = keyof ProjRow;

export default function DataStorage() {
  const qc = useQueryClient();
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["storage-overview"],
    queryFn: async () => (await api.get<Overview>("/data-browser/storage-overview")).data as Overview,
    staleTime: 5 * 60 * 1000,
  });
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "bytes", dir: -1 });
  const [q, setQ] = useState("");

  const view = useMemo(() => {
    if (!data) return null;
    const nameOf = new Map(data.projects.map(p => [p.project_id, p.name]));
    const nm = (id: string | null) => (id && nameOf.get(id)) || NO_PROJECT;
    const rows = new Map<string, ProjRow & { kwSet: Set<string> }>();
    const get = (n: string) => {
      if (!rows.has(n)) rows.set(n, { name: n, keywords: 0, groups: 0, serp: 0, last: null, over30: 0, serpUrls: 0, withHtml: 0, htmlUrls: 0, versions: 0, bytes: 0, kwSet: new Set() });
      return rows.get(n)!;
    };
    data.projects.forEach(p => get(p.name));
    for (const g of data.keyword_groups) {
      const r = get(nm(g.project_id));
      r.kwSet.add(g.keyword); r.groups += 1; r.serp += g.analyses; r.serpUrls += g.serp_urls; r.withHtml += g.urls_with_html;
      if (!r.last || g.last_at > r.last) r.last = g.last_at;
      if (daysSince(g.last_at) > 30) r.over30 += 1;
    }
    for (const h of data.html_by_project) { const r = get(nm(h.project_id)); r.htmlUrls += h.urls; r.versions += h.versions; r.bytes += h.stored_bytes; }
    const list: ProjRow[] = [...rows.values()].map(({ kwSet, ...r }) => ({ ...r, keywords: kwSet.size }));
    const sum = (k: keyof ProjRow) => list.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    const totalBytes = sum("bytes");
    const top = [...list].sort((a, b) => b.bytes - a.bytes)[0];
    const groups = data.keyword_groups.map(g => ({ ...g, project: nm(g.project_id), days: daysSince(g.last_at) }));
    const byProj = (xs?: { project_id: string | null; verified: number }[]) => {
      const m = new Map<string, number>(); (xs ?? []).forEach(x => m.set(nm(x.project_id), (m.get(nm(x.project_id)) ?? 0) + x.verified)); return m;
    };
    const ncb = byProj(data.archive?.by_project), drive = byProj(data.archive?.drive?.by_project);
    return { list, groups, ncb, drive, totals: { keywords: sum("keywords"), serp: sum("serp"), htmlUrls: sum("htmlUrls"), versions: sum("versions"), bytes: totalBytes, top, topShare: totalBytes ? top.bytes / totalBytes : 0 } };
  }, [data]);

  const sorted = useMemo(() => {
    if (!view) return [];
    return [...view.list].sort((a, b) => {
      const x = a[sort.key], y = b[sort.key];
      if (x == null) return 1; if (y == null) return -1;
      return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "zh-Hant")) * sort.dir;
    });
  }, [view, sort]);
  const groups = useMemo(() => {
    if (!view) return [];
    const s = q.trim().toLowerCase();
    return view.groups.filter(g => !s || `${g.project} ${g.keyword}`.toLowerCase().includes(s)).sort((a, b) => b.days - a.days);
  }, [view, q]);

  const head = (key: SortKey, label: string, right = true) => (
    <TableHead className={right ? "text-right" : ""}>
      <button className="hover:underline" onClick={() => setSort(s => ({ key, dir: s.key === key ? (-s.dir as 1 | -1) : (key === "name" ? 1 : -1) }))}>
        {label}{sort.key === key ? (sort.dir > 0 ? " ↑" : " ↓") : ""}
      </button>
    </TableHead>
  );
  const maxBytes = view ? Math.max(1, ...view.list.map(r => r.bytes)) : 1;
  const cat = data?.db.categories;
  const catTotal = cat ? cat.html + cat.serp + cat.analysis + cat.other : 0;
  const maxGb = Math.max(...STORAGE.map(s => s.gb));
  const ar = data?.archive;
  const ncbTotal = ar?.verified ?? 0, driveTotal = ar?.drive?.verified ?? 0;
  const at = (iso?: string) => (iso ? new Date(iso).toLocaleString("zh-TW") : "—");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">資料與備份</h1>
          <p className="text-sm text-muted-foreground">
            各專案的字詞、SERP、HTML 與容量，以及資料放在哪裡、何時轉移備份。
            {data && <> 資料庫數字即時（{new Date(data.generated_at).toLocaleString("zh-TW")}）；主機與雲端容量為 {SNAPSHOT_AT} 量測。</>}
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={isFetching}
          onClick={async () => { await api.get("/data-browser/storage-overview?refresh=1"); qc.invalidateQueries({ queryKey: ["storage-overview"] }); }}>
          {isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}重新計算
        </Button>
      </div>

      {isLoading && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />載入中…</div>}
      {error && <Card><CardContent className="flex items-center gap-2 p-4 text-destructive"><AlertTriangle className="h-4 w-4" />無法讀取資料：{(error as Error).message}</CardContent></Card>}

      {view && (
        <>
          <p className="text-sm">
            HTML／抓取佔資料庫 <b>{cat ? pct(cat.html / catTotal) : "—"}</b>；已送 NCB 並讀回比對通過 <b className={ncbTotal ? "" : "text-destructive"}>{fmt(ncbTotal)}</b> 筆（共 {fmt(view.totals.versions)}）；
            Google Drive 逐頁複本 <b>{fmt(driveTotal)}</b> 筆（另有每日整庫備份含全部）；
            最大的專案 <b>{view.totals.top?.name}</b> 佔 HTML 容量 <b>{pct(view.totals.topShare)}</b>。
          </p>

          <div className="grid gap-4 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
            {[
              ["字詞數", fmt(view.totals.keywords), "各專案不重複關鍵字加總"],
              ["SERP 分析組數", fmt(view.totals.serp), "重跑會再算一次"],
              ["已存 HTML 的網址", fmt(view.totals.htmlUrls), "各專案不重複網址加總"],
              ["HTML 版本筆數", fmt(view.totals.versions), "同網址每抓一次存一筆"],
              ["HTML 在資料庫（GB）", gb(view.totals.bytes), "壓縮後實際佔用"],
              ["已送 NCB（讀回比對通過）", fmt(ncbTotal), ar ? `失敗 ${fmt(ar.failed)}｜狀態 ${at(ar.generated_at)}` : "讀不到封存狀態"],
            ].map(([t, v, s], i) => (
              <Card key={t}><CardHeader className="pb-2"><CardDescription>{t}</CardDescription>
                <CardTitle className={`text-2xl tabular-nums ${i === 5 && !ncbTotal ? "text-destructive" : ""}`}>{v}</CardTitle></CardHeader>
                <CardContent className="pt-0 text-xs text-muted-foreground">{s}</CardContent></Card>
            ))}
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">專案總覽</CardTitle>
              <CardDescription>點欄名排序；色條＝資料庫內 HTML 容量。SERP 網址為各組加總，同網址在多組會重複計。</CardDescription></CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  {head("name", "專案", false)}{head("keywords", "字詞")}{head("serp", "SERP 組數")}{head("last", "最近 SERP")}{head("over30", "超過 30 天")}
                  {head("serpUrls", "SERP 網址")}{head("withHtml", "已抓 HTML 網址")}<TableHead className="text-right">覆蓋率</TableHead>
                  {head("htmlUrls", "存 HTML 網址")}{head("versions", "HTML 版本")}{head("bytes", "資料庫 MB")}
                  <TableHead className="text-right">NCB</TableHead><TableHead className="text-right">Google Drive 逐頁</TableHead>
                  <TableHead className="text-right">缺漏 NCB／Drive</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {sorted.map(r => (
                    <TableRow key={r.name}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r.keywords)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r.serp)}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.last ? r.last.slice(0, 10) : "—"}</TableCell>
                      <TableCell className={`text-right tabular-nums ${r.over30 > 0 ? "text-amber-600" : ""}`}>{fmt(r.over30)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r.serpUrls)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r.withHtml)}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.serpUrls ? pct(r.withHtml / r.serpUrls) : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r.htmlUrls)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r.versions)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span className="inline-flex items-center justify-end gap-2">
                          <span className={`inline-block h-2 rounded ${r.bytes === maxBytes ? "bg-destructive" : "bg-muted-foreground/40"}`} style={{ width: `${Math.max(2, 80 * r.bytes / maxBytes)}px` }} />
                          {mb(r.bytes)}
                        </span>
                      </TableCell>
                      {(() => {
                        const n = view.ncb.get(r.name) ?? 0, g = view.drive.get(r.name) ?? 0;
                        const mn = Math.max(0, r.versions - n), mg = Math.max(0, r.versions - g);
                        return (<>
                          <TableCell className={`text-right tabular-nums ${mn ? "text-destructive" : ""}`}>{fmt(n)}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmt(g)}</TableCell>
                          <TableCell className={`text-right tabular-nums ${mn || mg ? "text-amber-600" : "text-muted-foreground"}`}>{fmt(mn)}／{fmt(mg)}</TableCell>
                        </>);
                      })()}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="mt-2 text-xs text-muted-foreground">NCB＝已寫入並逐頁讀回、指紋與原 HTML 相同的筆數。Google Drive 逐頁＝已上傳並比對檔案雜湊、且指紋與 NCB 相同的筆數；另有每日整庫備份（含全部）。缺漏＝資料庫 HTML 版本數減去該處已確認筆數。</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">逐頁備份確認（NCB／Google Drive）</CardTitle>
              <CardDescription>{ar ? <>每 10 分鐘更新，最近 {at(ar.generated_at)}；{ar.daily_sync}</> : "讀不到封存狀態檔（VPS 每 10 分鐘寫入一次）"}</CardDescription></CardHeader>
            {ar && (
              <CardContent className="space-y-4 text-sm">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div><div className="text-muted-foreground">NCB 讀回比對通過</div><div className="text-xl font-semibold tabular-nums">{fmt(ar.verified)}<span className="text-sm font-normal text-muted-foreground">／{fmt(ar.html_versions_total ?? view.totals.versions)}</span></div>
                    <div className={`text-xs ${ar.failed ? "text-destructive" : "text-muted-foreground"}`}>失敗 {fmt(ar.failed)}｜最近寫入 {at(ar.last_archived_at)}</div></div>
                  <div><div className="text-muted-foreground">Google Drive 逐頁複本</div><div className="text-xl font-semibold tabular-nums">{fmt(driveTotal)}<span className="text-sm font-normal text-muted-foreground">／{fmt(ar.verified)}</span></div>
                    <div className={`text-xs ${ar.drive?.not_verified ? "text-destructive" : "text-muted-foreground"}`}>未通過 {fmt(ar.drive?.not_verified ?? 0)}｜最近上傳 {at(ar.drive?.last_uploaded_at)}</div></div>
                  <div><div className="text-muted-foreground">等待中（新抓頁面）</div><div className="text-xl font-semibold tabular-nums">{fmt((ar.sync?.ncb_due ?? 0) + (ar.sync?.ncb_waiting_settle ?? 0))}</div>
                    <div className="text-xs text-muted-foreground">最近檢查 {at(ar.sync?.last_check_at)}</div></div>
                </div>
                <div className="space-y-2">
                  {ar.databases.map(db => (
                    <div key={db.instance} className="grid grid-cols-[minmax(8rem,12rem)_1fr_auto] items-center gap-3">
                      <span>{db.name}{db.bound ? "" : <span className="text-xs text-muted-foreground">（備用）</span>}</span>
                      <div className="h-3 rounded bg-muted"><div className={`h-3 rounded ${db.level === "full" ? "bg-destructive" : db.level === "warn" ? "bg-amber-500" : "bg-primary/70"}`} style={{ width: `${Math.min(100, 100 * db.rows / ar.row_limit)}%` }} /></div>
                      <span className={`tabular-nums whitespace-nowrap ${db.level !== "ok" ? "font-semibold text-amber-600" : ""}`}>{fmt(db.rows)}／{fmt(ar.row_limit)}{db.level === "warn" ? " 將滿，請換下一個庫" : db.level === "full" ? " 已滿" : ""}</span>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">NCB 每庫上限 {fmt(ar.row_limit)} 筆（一頁約 3 筆）；到 {fmt(ar.warn_at)} 筆會在這裡提醒換庫。</p>
                </div>
              </CardContent>
            )}
          </Card>

          <HtmlArchiveSearchCard />

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">儲存位置與容量</CardTitle><CardDescription>GB；{SNAPSHOT_AT} 量測</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                {STORAGE.map(s => (
                  <div key={s.item} className="grid grid-cols-[minmax(8rem,14rem)_1fr] items-center gap-3 text-sm">
                    <div><div className="flex items-center gap-1"><s.icon className="h-3.5 w-3.5 text-muted-foreground" />{s.item}</div>
                      <div className="text-xs text-muted-foreground">{s.where}｜{s.note}</div></div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 rounded bg-primary/70" style={{ width: `${s.gb ? Math.max(1, 100 * s.gb / maxGb) : 0}%` }} />
                      <span className={`tabular-nums whitespace-nowrap ${s.gb === 0 ? "font-semibold text-destructive" : ""}`}>{s.gb.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">APP04 資料庫組成</CardTitle>
                <CardDescription>即時；資料庫共 {data ? gb(data.db.total_bytes) : "—"} GB</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                {cat && (
                  <>
                    <div className="flex h-6 overflow-hidden rounded border">
                      <div className="bg-primary" style={{ width: pct(cat.html / catTotal) }} />
                      <div className="bg-sky-500" style={{ width: pct(cat.serp / catTotal) }} />
                      <div className="bg-emerald-500" style={{ width: pct(cat.analysis / catTotal) }} />
                      <div className="bg-muted-foreground/40" style={{ width: pct(cat.other / catTotal) }} />
                    </div>
                    {([["HTML／抓取", cat.html, "bg-primary"], ["SERP 資料", cat.serp, "bg-sky-500"], ["分析報告", cat.analysis, "bg-emerald-500"], ["其他", cat.other, "bg-muted-foreground/40"]] as const).map(([n, b, c]) => (
                      <div key={n} className="flex items-center gap-2 text-sm"><span className={`inline-block h-3 w-3 rounded-sm ${c}`} />{n}<span className="ml-auto tabular-nums">{gb(b)} GB（{pct(b / catTotal)}）</span></div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">轉移備份時程與機制</CardTitle>
              <CardDescription>舊 HTML 移到外部：先修 NCB、先寫後驗、最後才刪本機。時間以前一步完成為起點。</CardDescription></CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>步驟</TableHead><TableHead>做什麼</TableHead><TableHead>狀態</TableHead><TableHead>時間</TableHead></TableRow></TableHeader>
                <TableBody>
                  {PLAN.map(p => (
                    <TableRow key={p.step}>
                      <TableCell className="font-medium whitespace-nowrap">{p.step}</TableCell><TableCell>{p.item}</TableCell>
                      <TableCell><Badge variant={p.status === "完成" ? "default" : "outline"} className={p.status.startsWith("等") ? "border-amber-500 text-amber-600" : p.status === "啟用中" ? "border-emerald-500 text-emerald-600" : ""}>{p.status}</Badge></TableCell>
                      <TableCell className="whitespace-nowrap">{p.when}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">關鍵字組明細</CardTitle>
              <CardDescription>每組＝專案＋關鍵字＋地區＋語言；距今超過 30 天以橘色標示，依距今天數排序。</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <Input placeholder="搜尋關鍵字或專案" value={q} onChange={e => setQ(e.target.value)} className="max-w-sm" />
              <div className="max-h-[32rem] overflow-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>專案</TableHead><TableHead>關鍵字</TableHead><TableHead>地區／語言</TableHead>
                    <TableHead className="text-right">SERP 組數</TableHead><TableHead className="text-right">最近 SERP</TableHead><TableHead className="text-right">距今天數</TableHead>
                    <TableHead className="text-right">SERP 網址</TableHead><TableHead className="text-right">已抓 HTML</TableHead><TableHead className="text-right">LLM 步數</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {groups.map(g => (
                      <TableRow key={`${g.project}|${g.keyword}|${g.location_code}|${g.language_code}`}>
                        <TableCell className="whitespace-nowrap">{g.project}</TableCell><TableCell>{g.keyword}</TableCell>
                        <TableCell className="whitespace-nowrap">{REGION[g.location_code] ?? g.location_code}／{g.language_code}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(g.analyses)}</TableCell>
                        <TableCell className="text-right tabular-nums">{g.last_at.slice(0, 10)}</TableCell>
                        <TableCell className={`text-right tabular-nums ${g.days > 30 ? "text-amber-600 font-medium" : ""}`}>{fmt(g.days)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(g.serp_urls)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(g.urls_with_html)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(g.llm_steps)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
